/**
 * Centralized task type labels - Single Source of Truth
 *
 * All task type display names should use this utility to ensure consistency.
 * Pattern: Verb + Noun (action-first)
 */

export type TaskLabelContext = 'badge' | 'activity' | 'progress'

interface TaskLabels {
  badge: string
  activity: string
  progress: string
}

const TASK_LABELS: Record<string, TaskLabels> = {
  // Current task types
  listing_generation: {
    badge: 'Generate Listing',
    activity: 'Generated listing',
    progress: 'Generating listing',
  },
  thumbnail_generation: {
    badge: 'Generate Thumbnail',
    activity: 'Generated thumbnail',
    progress: 'Generating thumbnail',
  },
  design_check: {
    badge: 'Check Image',
    activity: 'Checked image',
    progress: 'Checking image',
  },
  listing_check: {
    badge: 'Check Listing',
    activity: 'Checked listing',
    progress: 'Checking listing',
  },
  field_generation: {
    badge: 'Generate Field',
    activity: 'Generated field',
    progress: 'Generating field',
  },

  // Legacy types - map to current equivalents
  field_regeneration: {
    badge: 'Generate Field',
    activity: 'Generated field',
    progress: 'Generating field',
  },
  recheck: {
    badge: 'Check Listing',
    activity: 'Checked listing',
    progress: 'Checking listing',
  },
  regeneration: {
    badge: 'Generate Listing',
    activity: 'Generated listing',
    progress: 'Generating listing',
  },
}

/**
 * Get a display label for a task type.
 *
 * @param taskType - The task type from the API (e.g., 'listing_generation')
 * @param context - The display context:
 *   - 'badge': Short label for badges (e.g., "Generate Listing")
 *   - 'activity': Past tense for activity feeds (e.g., "Generated listing")
 *   - 'progress': Present participle for in-progress states (e.g., "Generating listing")
 * @returns The formatted label string
 */
export function getTaskLabel(taskType: string, context: TaskLabelContext = 'badge'): string {
  const labels = TASK_LABELS[taskType]
  if (labels) {
    return labels[context]
  }

  // Fallback: title case the type with spaces
  const fallback = taskType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

  return fallback
}
